<template>
  <v-container>
    <v-row
      align="center"
      justify="center"
    >
      <v-col
        cols="12"
        md="6"
        align="center"
      >
        <v-card
          align="left"
          max-width="400"
          class="pa-4 no-truncate"
        >
          <v-img
            :src="culColour"
            class="mb-4"
          />

          <template v-if="!emailEntered">
            <v-card-title>
              <v-icon
                size="64"
                class="me-4"
                color="grey"
              >
                mdi-email-fast
              </v-icon>
              Request Sign-in Code
            </v-card-title>
            <v-card-text class="mb-2 subtitle-wrap">
              Enter your email address to receive a one-time code to sign in to the Deposit Service.
            </v-card-text>

            <v-form
              v-model="isEmailValid"
              @submit.prevent="submit"
            >
              <v-text-field
                v-model="email"
                :rules="emailRules"
                label="Email address"
                type="email"
                autocomplete="email"
              />
              <v-card-actions>
                <v-btn
                  v-if="isEmailValid"
                  block
                  :variant="isEmailValid ? 'elevated' : 'flat'" 
                  :color="isEmailValid ? 'secondary' : undefined"
                  :disabled="!isEmailValid || busy"
                  :class="{ 'text-white': isEmailValid }"
                  size="x-large"
                  @click="getEmail"
                >
                  <span>SEND ME A SIGN-IN CODE</span>
                  <v-progress-circular
                    v-if="busy"
                    indeterminate
                    class="ms-3"
                  />
                </v-btn>
              </v-card-actions>
            </v-form>

            <v-container class="text-center">
              <v-row
                align="center"
                justify="center"
                class="my-4"
              >
                <v-divider class="mx-4" />or<v-divider class="mx-4" />
              </v-row>
              <v-btn
                block
                width="100%"
                @click="adminLogin"
              >
                <v-img
                  :src="ravenLogo"
                  alt="Raven logo"
                  height="24"
                  width="24"
                  class="mx-3"
                />
                <span title="Admin login">Admin login</span>
              </v-btn>
            </v-container>
          </template>

          <template v-else>
            <v-card-title>Sign in</v-card-title>
            <v-card-text class="mb-2 subtitle-wrap">
              If your email address is registered with the service, you will receive a one-time code.
            </v-card-text>

            <v-form
              v-model="codeEntered"
              @submit.prevent="submit"
            >
              <v-otp-input
                v-model="code"
                class="ma-2"
                required
                length="6"
                type="number"
                @on-change="handleOnChange"
                @on-complete="handleOnComplete"
              />
              <v-card-actions>
                <!-- Start over: outlined secondary -->
                <v-btn
                  variant="outlined"
                  color="secondary"
                  class="ma-2"
                  @click="restart"
                >
                  <v-icon class="me-2">
                    mdi-arrow-left
                  </v-icon>
                  <span title="Start over">Start over</span>
                </v-btn>

                <v-spacer />

                <!-- Continue: filled secondary with white text -->
                <v-btn
                  variant="flat"
                  color="secondary"
                  class="ma-2 text-white"
                  :disabled="code.length < 6 || busy"
                  :loading="busy"
                  @click="getCode"
                >
                  <span title="Continue">CONTINUE</span>
                </v-btn>
              </v-card-actions>

              <!-- Intentionally NOT showing raw errors on this page -->
            </v-form>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import culColour from '@/assets/cul_colour.png'
import ravenLogo from '@/assets/raven-logo-small.gif'
import { signIn, confirmSignIn, getCurrentUser, updateUserAttributes } from 'aws-amplify/auth'
import { Auth } from '@/utils/amplify-compat'
import { clearIdentityCache } from '@/utils/amplify-compat'

export default {
  name: 'SignIn',
  data () {
    return {
      busy: false,
      email: '',
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+\..+/.test(v) || 'E-mail must be valid'
      ],
      emailEntered: false,
      codeEntered: false,
      isEmailValid: false,
      culColour,
      ravenLogo,
      code: '',
      error: ''
    }
  },
  methods: {
    // single handler used by both forms
    async submit () {
      if (!this.emailEntered) return this.getEmail()
      return this.getCode()
    },
    async adminLogin () {
      try { clearIdentityCache() } catch (e) {
        console.error('Clearing identity cache failed:', e)
      }
      this.$cognitoAuth.redirectToSignin()
    },
    handleOnComplete (value) {
      this.codeEntered = true
      this.code = value
    },
    handleOnChange (value) {
      this.code = value
    },
    async getEmail () {
      try {
        this.busy = true
        this.error = ''
        Auth.clearIdentityCache()
        const out = await signIn({
          username: this.email,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' }
        })

        // v6 returns next step info; for custom challenge we expect the confirm step
        if (out.isSignedIn) {
          // some flows might sign in immediately
          this.$router.push('/deposits')
          return
        }

        const step = out.nextStep?.signInStep
        // Typical passwordless/custom challenge:
        if (step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' || step === 'CONFIRM_SIGN_IN_WITH_CODE') {
          this.emailEntered = true
          return
        }

        // For any other step (MFA etc.), still show the code panel if appropriate
        this.emailEntered = true
      } catch (err) {
        // Don’t show raw error text on the page
        console.warn('[signIn] error', err)
        // Optionally: show a toast/snackbar elsewhere
      } finally {
        this.busy = false
      }
    },
    async getCode () {
      try {
        this.busy = true
        this.error = ''
        await confirmSignIn({ challengeResponse: this.code })

        // Proactively remove any guest IdentityId from earlier accidental calls
        Auth.clearIdentityCache()

        // After successful confirm, mint authenticated Identity Pool creds.
        // 1) Try once normally (wrapper already retries on mismatch)
       try {
        const creds = await Auth.currentCredentials()
        console.log('[signin] identityId', creds.identityId)
        try {
          const user = await getCurrentUser()
          await updateUserAttributes({
            user,
            userAttributes: { 'custom:identityid': creds.identityId }
          })
          console.log('[signin] set custom:identityid on user')
        } catch (e) {
          console.warn('[signin] failed to set custom:identityid (will still sign in):', e)
        }
      } catch (e) {
        // 2) If the mismatch happened *before* our wrapper got to clear it, do it here:
        if (e?.name === 'NotAuthorizedException' && /logins don'?t match/i.test(String(e?.message))) {
          Auth.clearIdentityCache()
          // tiny pause so Amplify re-reads storage
          await new Promise(r => setTimeout(r, 10))
          const creds = await Auth.currentCredentials()
          console.log('[signin/after-clear] identityId', creds.identityId)
        } else {
          throw e
        }
      }
      this.$router.push('/deposits')
      } catch (err) {
        // If the confirm session is gone (page refreshed, multiple clicks, etc.), reset gracefully
        if (String(err?.name) === 'SignInException') {
          console.warn('[confirmSignIn] session lost; restarting flow')
          await this.restart()
          return
        }
        console.warn('[confirmSignIn] error', err)
      } finally {
        this.busy = false
      }
    },
    async restart () {
      this.busy = true
      // drop any stale IdentityId that could confuse the next sign-in
      Auth.clearIdentityCache()
      this.emailEntered = false
      this.codeEntered = false
      this.email = ''
      this.code = ''
      this.isEmailValid = false
      this.error = ''
      this.busy = false
    },
  }
}
</script>

<style>
.no-truncate, .no-truncate * {
  min-width: 0;              /* let flex children wrap */
}

/* belt & braces: nuke ellipsis anywhere in the block */
.subtitle-wrap, .subtitle-wrap * {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  overflow-wrap: anywhere;
  word-break: normal;
  display: block;            /* ensure multi-line layout */
}
</style>